import { AnyAction } from 'redux';
import { DefaultMapping, SupplierMapping } from '@synergy/models';
import { PaginatedResponse } from '@synergy/api';
import { sortByProperty } from '@synergy/utils';

import { Attribute } from '../models/attribute';
import {
    CANCEL_CREATE_ATTRIBUTE,
    CANCEL_DELETE_ATTRIBUTE,
    CANCEL_PATCH_ATTRIBUTE,
    CANCEL_UPDATE_MAPPING,
    CREATE_ATTRIBUTE_COMPLETED,
    DELETE_ATTRIBUTE_COMPLETED,
    PATCH_ATTRIBUTE_COMPLETED,
    RECEIVE_ATTRIBUTES,
    RECEIVE_ATTRIBUTE_DEFAULT_MAPPING,
    RECEIVE_ATTRIBUTE_DEFAULT_MAPPINGS,
    REQUEST_ATTRIBUTES,
    REQUEST_ATTRIBUTE_MAPPINGS,
    START_CREATE_ATTRIBUTE,
    START_DELETE_ATTRIBUTE,
    START_PATCH_ATTRIBUTE,
    START_UPDATE_MAPPING,
} from './../actions/attributes-actions';

export interface AttributesReducerState {
    items: Attribute[];
    sortedByName: Attribute[];
    defaultMappings: PaginatedResponse<DefaultMapping<Attribute>>;
    supplierMappings: PaginatedResponse<SupplierMapping<Attribute>>;
    page: number;
    limit: number;
    total: number;
    requesting: boolean;
}

export const initialAttributesReducerState: AttributesReducerState = {
    items: [],
    sortedByName: [],
    defaultMappings: { items: [], limit: 10, page: 0, total: 0 },
    supplierMappings: { items: [], limit: 10, page: 0, total: 0 },
    page: 0,
    limit: 0,
    total: 0,
    requesting: false,
};

export const attributesReducer = (
    state: AttributesReducerState = initialAttributesReducerState,
    action: AnyAction
): AttributesReducerState => {
    const { type, ...rest } = action;

    switch (type) {
        case REQUEST_ATTRIBUTES:
        case REQUEST_ATTRIBUTE_MAPPINGS:
        case START_UPDATE_MAPPING:
        case START_DELETE_ATTRIBUTE:
        case START_PATCH_ATTRIBUTE:
        case START_CREATE_ATTRIBUTE: {
            return {
                ...state,
                requesting: true,
            };
        }

        case CANCEL_UPDATE_MAPPING:
        case CANCEL_CREATE_ATTRIBUTE:
        case CANCEL_DELETE_ATTRIBUTE:
        case CANCEL_PATCH_ATTRIBUTE: {
            return {
                ...state,
                requesting: false,
            };
        }

        case RECEIVE_ATTRIBUTES: {
            const sortedAttributes = sortByProperty({ data: rest['attributesPaginatedData'].items, key: 'name' });
            return {
                ...state,
                ...rest['attributesPaginatedData'],
                sortedByName: sortedAttributes,
                requesting: false,
            };
        }

        case RECEIVE_ATTRIBUTE_DEFAULT_MAPPING: {
            const items = [...state.defaultMappings.items];
            const index = items.findIndex(mapping => mapping.id === rest['defaultMapping'].id);
            if (index >= 0) {
                items[index] = rest['defaultMapping'];
            }

            return {
                ...state,
                defaultMappings: { ...state.defaultMappings, items },
                requesting: false,
            };
        }

        case RECEIVE_ATTRIBUTE_DEFAULT_MAPPINGS: {
            return {
                ...state,
                defaultMappings: rest['defaultMappings'],
                requesting: false,
            };
        }

        case PATCH_ATTRIBUTE_COMPLETED: {
            const newAttributes = [...state.items];

            const attributeIndex = newAttributes.findIndex(attribute => attribute.id === rest['attribute'].id);
            if (attributeIndex === -1) {
                return state;
            }
            newAttributes[attributeIndex] = rest['attribute'];

            return {
                ...state,
                items: newAttributes,
                requesting: false,
            };
        }

        case CREATE_ATTRIBUTE_COMPLETED: {
            return {
                ...state,
                items: [...state.items, rest['attribute']],
                requesting: false,
            };
        }

        case DELETE_ATTRIBUTE_COMPLETED: {
            const newAttributes = [...state.items];
            const attributeIndex = newAttributes.findIndex(attribute => attribute.id === rest['attributeId']);

            if (attributeIndex === -1) {
                return state;
            }

            newAttributes.splice(attributeIndex, 1);

            return {
                ...state,
                items: newAttributes,
                requesting: false,
            };
        }

        default: {
            return state;
        }
    }
};
